import { supabase } from "../supabaseClient";

const API_BASE = process.env.REACT_APP_API_URL || "";
const STORAGE_KEY = "clearbadge_affiliate_referral";
const COOKIE_NAME = "cb_affiliate_referral";
const DEFAULT_WINDOW_DAYS = 60;

const normalizeAffiliateCode = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, "")
    .slice(0, 64);

const safeJsonParse = (value) => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const readCookie = (name) => {
  if (typeof document === "undefined") return "";
  const target = String(name || "").trim();
  const parts = (document.cookie || "").split(";");
  for (const part of parts) {
    const [rawKey, ...rest] = String(part || "").split("=");
    const key = rawKey?.trim();
    if (key !== target) continue;
    const rawValue = rest.join("=").trim();
    try {
      return decodeURIComponent(rawValue);
    } catch {
      return rawValue;
    }
  }
  return "";
};

const writeCookie = (name, value, expiresAt) => {
  if (typeof document === "undefined") return;
  const maxAgeSeconds = Math.max(60, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000));
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${maxAgeSeconds}; Path=/; SameSite=Lax`;
};

const readDatafastCookie = (name) => readCookie(name);

const persistReferral = (referral) => {
  if (typeof window === "undefined") return null;
  const code = normalizeAffiliateCode(referral?.code);
  if (!code) return null;
  const nowIso = new Date().toISOString();
  const expiresAt =
    referral?.expiresAt ||
    new Date(Date.now() + DEFAULT_WINDOW_DAYS * 24 * 60 * 60 * 1000).toISOString();
  const payload = {
    code,
    clickId: referral?.clickId || "",
    capturedAt: referral?.capturedAt || nowIso,
    expiresAt,
  };
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  writeCookie(COOKIE_NAME, JSON.stringify(payload), expiresAt);
  return payload;
};

export const readStoredAffiliateReferral = () => {
  if (typeof window === "undefined") return null;
  const fromStorage = safeJsonParse(window.localStorage.getItem(STORAGE_KEY) || "");
  const fromCookie = safeJsonParse(readCookie(COOKIE_NAME) || "");
  const referral = fromStorage || fromCookie;
  const code = normalizeAffiliateCode(referral?.code);
  if (!code) return null;
  const expiresAt = new Date(referral?.expiresAt || 0).getTime();
  if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
    window.localStorage.removeItem(STORAGE_KEY);
    return null;
  }
  return { ...referral, code };
};

export const captureAffiliateReferral = async ({ code, landingPath = "", referrer = "" } = {}) => {
  const affiliateCode = normalizeAffiliateCode(code);
  if (!affiliateCode) return null;

  const fallbackReferral = persistReferral({ code: affiliateCode });
  try {
    const res = await fetch(`${API_BASE}/api/affiliate/click`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        code: affiliateCode,
        landingPath,
        referrer,
        datafastVisitorId: readDatafastCookie("datafast_visitor_id"),
        datafastSessionId: readDatafastCookie("datafast_session_id"),
      }),
    });
    const payload = await res.json().catch(() => ({}));
    if (!res.ok) return fallbackReferral;
    return persistReferral({
      code: payload?.affiliate?.code || affiliateCode,
      clickId: payload?.clickId || "",
      expiresAt: payload?.expiresAt || "",
      capturedAt: new Date().toISOString(),
    });
  } catch {
    return fallbackReferral;
  }
};

export const getAffiliateCodeFromSearch = (search = "") => {
  const params = new URLSearchParams(search || "");
  return normalizeAffiliateCode(
    params.get("ref") ||
      params.get("affiliate") ||
      params.get("affiliate_code") ||
      params.get("via") ||
      params.get("cb_ref") ||
      ""
  );
};

export const getAffiliateSignUpMetadata = () => {
  const referral = readStoredAffiliateReferral();
  if (!referral?.code) return {};
  return {
    affiliate_code: referral.code,
    affiliate_click_id: referral.clickId || "",
    affiliate_captured_at: referral.capturedAt || "",
  };
};

export const attributeAffiliateSignup = async (session) => {
  const referral = readStoredAffiliateReferral();
  if (!referral?.code) return null;
  const accessToken = session?.access_token;
  if (!accessToken) return null;

  const res = await fetch(`${API_BASE}/api/affiliate/attribute-signup`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({
      code: referral.code,
      clickId: referral.clickId || "",
      capturedAt: referral.capturedAt || "",
    }),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(payload?.error || "Unable to attribute affiliate signup.");
  }
  return payload;
};

export const attributeCurrentAffiliateSignup = async () => {
  const { data } = await supabase.auth.getSession();
  return attributeAffiliateSignup(data?.session);
};
